import { useState, useMemo } from 'react';
import {
    Container,
    Text,
    Group,
    Select, Stack,
    Paper, RingProgress,
    Center,
    Alert,
    Card,
    Title,
    Badge,
    Progress,
    ActionIcon,
    Collapse,
    Box,
    Skeleton
} from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import { PageHeader } from '../common/PageHeader';
import { MilestoneDateLabels, milestoneHasSecondaryDateLabel, MILESTONE_SECONDARY_DATE_ROW_OFFSET } from './MilestoneDateLabels';
import { InteractiveMilestoneStatusBadge } from './InteractiveMilestoneStatusBadge';
import { Objective, KeyActivity, Activity, Milestone, MilestoneStatus } from '../../types/milestones';
import { useGroupQuery } from '../../hooks/useGroupQuery';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { milestonesApi } from '../../api/milestones';
import { showSuccessNotification, showApiErrorNotification } from '../../utils/notifications';
import { formatMilestonesLastUpdatedLabel } from '../../utils/milestoneFreshness';
import { getMilestonePastDueAccent } from '../../utils/milestonePastDue';

// Status configuration
const statusConfig = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' }
};

// New component for displaying milestones with status selector
const MilestoneCard = ({ milestone, activityId, goalId, keyActivityId, objectiveId, onStatusChange }: { 
  milestone: Milestone; 
  activityId: string;
  goalId: string;
  keyActivityId: string;
  objectiveId: string;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
}) => {
  const pastDueAccent = getMilestonePastDueAccent(milestone.status, milestone.due_date);
  const hasSecondaryDateLabel = milestoneHasSecondaryDateLabel(milestone);

  // Extract numbers from activity_id and milestone id to create "1.1" format
  const getActivityNumber = (activityId: string): string => {
    const match = activityId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const getMilestoneNumber = (milestoneId: string): string => {
    const match = milestoneId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const activityNumber = getActivityNumber(activityId);
  const milestoneNumber = getMilestoneNumber(milestone.id);
  const milestoneLabel = `${activityNumber}.${milestoneNumber}`;
  
    return (
    <Paper 
      shadow="xs" 
      p="md" 
      radius="md" 
      mb="xs"
      ml="lg"
      aria-label={pastDueAccent ? `Milestone ${milestoneLabel}, Past Due` : undefined}
      sx={(theme) => ({
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        ...(pastDueAccent
          ? { borderLeft: `3px solid ${theme.colors[pastDueAccent][6]}` }
          : {}),
        '&:hover': {
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]
        },
        '@media (max-width: 768px)': {
          marginLeft: theme.spacing.sm,
          padding: theme.spacing.sm,
        }
      })}
    >
        <Stack
          spacing="sm"
          pb={hasSecondaryDateLabel ? 0 : MILESTONE_SECONDARY_DATE_ROW_OFFSET}
        >          <Group position="apart" spacing="md" noWrap sx={(theme) => ({
            '@media (max-width: 768px)': {
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: theme.spacing.sm
            }
          })}>
            <Stack spacing={5} style={{ flex: 1, minWidth: 0 }}>
              <Group spacing="xs" mb={4}>
                <Badge size="xs" color="gray" variant="filled">{`Milestone ${milestoneLabel}`}</Badge>
              </Group>
            <Text 
              size="sm" 
              weight={600}
              sx={(theme) => ({
                textDecoration: milestone.status === 'completed' ? 'line-through' : 'none',
                color: milestone.status === 'completed' ? theme.colors.gray[6] : 'inherit',
                lineClamp: 2,
                '@media (max-width: 768px)': {
                  fontSize: theme.fontSizes.xs,
                  lineClamp: 'unset',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }
              })}
            >
              {milestone.title}
            </Text>
          </Stack>
          <Stack spacing="xs" align="flex-end" sx={(theme) => ({
            '@media (max-width: 768px)': {
              alignItems: 'flex-start'
            }
          })}>
            <MilestoneDateLabels milestone={milestone} />
            <Stack spacing="xs" align="flex-end" sx={(theme) => ({
              '@media (max-width: 768px)': {
                alignItems: 'flex-start'
              }
            })}>
              <InteractiveMilestoneStatusBadge
                status={milestone.status}
                statusUpdatedAt={
                  milestone.status_updated_at ??
                  (milestone as { statusUpdatedAt?: string }).statusUpdatedAt
                }
                onStatusChange={(status) => {
                  onStatusChange(`${milestone.id}:${activityId}:${goalId}:${keyActivityId}:${objectiveId}`, status);
                }}
              />
            </Stack>
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
};

const ActivityCard = ({ activity, goalId, keyActivityId, objectiveId, onStatusChange }: { 
  activity: Activity; 
  goalId: string;
  keyActivityId: string;
  objectiveId: string;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
}) => {
  const [expanded, setExpanded] = useState(true);
  
  const completedCount = activity.milestones.filter(m => m.status === 'completed').length;
  const completionPercentage = activity.milestones.length > 0 
    ? Math.round((completedCount / activity.milestones.length) * 100) 
    : 0;
  
  return (
    <Box mb="md" ml="lg">
      <Stack spacing="xs">
        {/* Activity Label - aligned with text content */}
        <Badge size="sm" color="cyan" variant="light" style={{ alignSelf: 'flex-start', marginLeft: '32px' }}>
          ACTIVITY
        </Badge>
        
        <Group position="apart" spacing="md" noWrap sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing.xs
          }
        })}>
          <Group spacing="xs" noWrap style={{ flex: 1 }}>
            <ActionIcon 
              onClick={() => setExpanded(!expanded)} 
              aria-label={expanded ? 'Collapse activity' : 'Expand activity'}
            >
              {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </ActionIcon>
            <Text weight={500} size="sm">
              {activity.title}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" sx={(theme) => ({
            '@media (max-width: 768px)': {
              marginLeft: theme.spacing.xl
            }
          })}>{`${completedCount}/${activity.milestones.length} complete`}</Text>
        </Group>
        
        <Progress 
          value={completionPercentage} 
          size="sm" 
          radius="xl" 
          color={completionPercentage === 100 ? 'teal' : 'blue'}
        />
        
        <Collapse in={expanded}>
          <Stack spacing="xs" mt="xs">
            {activity.milestones.length > 0 ? (
              activity.milestones.map((milestone) => (
                <MilestoneCard
                  key={`${objectiveId}:${keyActivityId}:${goalId}:${activity.id}:${milestone.id}`}
                  milestone={milestone}
                  activityId={activity.id}
                  goalId={goalId}
                  keyActivityId={keyActivityId}
                  objectiveId={objectiveId}
                  onStatusChange={onStatusChange}
                />
              ))
            ) : (
              <Text size="sm" color="dimmed" italic>No milestones found for this activity</Text>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};



const KeyActivityCard = ({ keyActivity, objectiveId, onStatusChange }: { 
  keyActivity: KeyActivity; 
  objectiveId: string;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
}) => {
  const [expanded, setExpanded] = useState(true);
  
  const allMilestones = keyActivity.goals.flatMap(goal => 
    goal.activities.flatMap(activity => activity.milestones)
  );
  const completedCount = allMilestones.filter(m => m.status === 'completed').length;
  const completionPercentage = allMilestones.length > 0 
    ? Math.round((completedCount / allMilestones.length) * 100) 
    : 0;
  
  return (
    <Box mb="md" ml="lg">
      <Stack spacing="xs">
        {/* Key Activity Label - aligned with text content */}
        <Badge size="md" color="indigo" variant="light" style={{ alignSelf: 'flex-start', marginLeft: '32px' }}>
          KEY ACTIVITY
        </Badge>
        
        <Group position="apart" spacing="md" noWrap sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing.xs
          }
        })}>
          <Group spacing="xs" noWrap style={{ flex: 1 }}>
            <ActionIcon 
              onClick={() => setExpanded(!expanded)} 
              aria-label={expanded ? 'Collapse key activity' : 'Expand key activity'}
            >
              {expanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
            </ActionIcon>
            <Text weight={600} size="md">
              {keyActivity.title}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" sx={(theme) => ({
            '@media (max-width: 768px)': {
              marginLeft: theme.spacing.xl
            }
          })}>{`${completedCount}/${allMilestones.length} milestones complete`}</Text>
        </Group>
        
        <Progress 
          value={completionPercentage} 
          size="sm" 
          radius="xl" 
          color={completionPercentage === 100 ? 'teal' : 'blue'}
        />
        
        <Collapse in={expanded}>
          <Stack spacing="md" mt="xs">
            {keyActivity.goals.flatMap((goal) => 
              goal.activities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  goalId={goal.id}
                  keyActivityId={keyActivity.id}
                  objectiveId={objectiveId}
                  onStatusChange={onStatusChange}
                />
              ))
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};

const ObjectiveCard = ({ objective, originalObjective, onStatusChange }: { 
  objective: Objective; 
  originalObjective: Objective;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
}) => {
  const [expanded, setExpanded] = useState(true);
  
  // Use originalObjective for completion calculations to get true percentage
  const allMilestones = originalObjective.key_activities.flatMap(ka => 
    ka.goals.flatMap(goal => 
      goal.activities.flatMap(activity => activity.milestones)
    )
  );
  const completedCount = allMilestones.filter(m => m.status === 'completed').length;
  const completionPercentage = allMilestones.length > 0 
    ? Math.round((completedCount / allMilestones.length) * 100) 
    : 0;
  
  return (
    <Card shadow="sm" p="xl" radius="lg" withBorder mb="xl" bg="rgba(0,0,0,0.02)">
      <Stack spacing="lg">
        <Group position="apart" spacing="md" noWrap>
          <Group spacing="md" noWrap style={{ flex: 1 }}>
            <ActionIcon 
              onClick={() => setExpanded(!expanded)} 
              aria-label={expanded ? 'Collapse objective' : 'Expand objective'}
              size="lg"
            >
              {expanded ? <IconChevronDown size={24} /> : <IconChevronRight size={24} />}
            </ActionIcon>
            <Stack spacing={4}>
              <Group spacing="md">
                {/* Objective Label first, then percent complete */}
                <Badge size="lg" color="violet" variant="light">
                  OBJECTIVE
                </Badge>
                <Badge color={completionPercentage === 100 ? 'green' : 'blue'} variant="filled">
                  {completionPercentage}% Complete
                </Badge>
              </Group>
              <Title order={3}>
                {objective.title}
              </Title>
              {objective.timeframe && (
                <Text size="sm" color="dimmed">
                  Timeframe: {objective.timeframe}
                </Text>
              )}
            </Stack>
          </Group>
        </Group>
        
        <Progress 
          value={completionPercentage} 
          size="lg" 
          radius="xl" 
          color={completionPercentage === 100 ? 'green' : 'blue'}
        />
        
        <Collapse in={expanded}>
          <Stack spacing="xl" mt="xs">
            {objective.key_activities.map((keyActivity) => (
              <KeyActivityCard
                key={keyActivity.id}
                keyActivity={keyActivity}
                objectiveId={objective.id}
                onStatusChange={onStatusChange}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
};

// Skeleton components for loading states
const MilestoneSkeleton = () => (
  <Paper p="md" shadow="xs" radius="md" mb="xs" ml="lg">
    <Stack spacing="sm">
      <Group position="apart" spacing="md" noWrap>
        <Stack spacing={5} style={{ flex: 1 }}>
          <Group spacing="xs" mb={4}>
            <Skeleton height={20} width={80} radius="xl" />
          </Group>
          <Skeleton height={16} width="85%" />
          <Skeleton height={16} width="70%" />
        </Stack>
        <Group spacing="xs" noWrap>
          <Skeleton height={16} width={16} />
          <Skeleton height={16} width={80} />
        </Group>
      </Group>
      <Group position="apart" spacing="md" noWrap>
        <Skeleton height={28} width={120} />
        <Group spacing="xs">
          <Skeleton height={24} width={60} radius="xl" />
        </Group>
      </Group>
    </Stack>
  </Paper>
);

const ActivitySkeleton = () => (
  <Box mb="md" ml="lg">
    <Stack spacing="xs">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="xs" noWrap style={{ flex: 1 }}>
          <Skeleton height={16} width={16} />
          <Skeleton height={20} width="60%" />
        </Group>
        <Skeleton height={16} width={100} />
      </Group>
      <Skeleton height={8} radius="xl" />
      <Stack spacing="xs" mt="xs">
        <MilestoneSkeleton />
        <MilestoneSkeleton />
        <MilestoneSkeleton />
      </Stack>
    </Stack>
  </Box>
);

const GoalSkeleton = () => (
  <Box mb="xl" ml="lg">
    <Stack spacing="md">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="xs" noWrap style={{ flex: 1 }}>
          <Skeleton height={20} width={20} />
          <Skeleton height={24} width="70%" />
        </Group>
        <Skeleton height={20} width={120} />
      </Group>
      <Skeleton height={12} radius="xl" />
      <Stack spacing="lg" mt="xs">
        <ActivitySkeleton />
        <ActivitySkeleton />
      </Stack>
    </Stack>
  </Box>
);

const KeyActivitySkeleton = () => (
  <Box mb="md" ml="lg">
    <Stack spacing="xs">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="xs" noWrap style={{ flex: 1 }}>
          <Skeleton height={18} width={18} />
          <Skeleton height={22} width="65%" />
        </Group>
        <Skeleton height={18} width={110} />
      </Group>
      <Skeleton height={8} radius="xl" />
      <Stack spacing="md" mt="xs">
        <GoalSkeleton />
      </Stack>
    </Stack>
  </Box>
);

const ObjectiveSkeleton = () => (
  <Card shadow="sm" p="xl" radius="lg" withBorder mb="xl" bg="rgba(0,0,0,0.02)">
    <Stack spacing="lg">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="md" noWrap style={{ flex: 1 }}>
          <Skeleton height={24} width={24} />
          <Stack spacing={4}>
            <Group spacing="xs">
              <Skeleton height={28} width={120} radius="xl" />
            </Group>
            <Skeleton height={32} width="80%" />
            <Skeleton height={20} width="40%" />
          </Stack>
        </Group>
      </Group>
      <Skeleton height={12} radius="xl" />
      <Stack spacing="xl" mt="xs">
        <KeyActivitySkeleton />
        <KeyActivitySkeleton />
      </Stack>
    </Stack>
  </Card>
);

export function MilestonesPage() {
  // Use group-dependent query for milestones data
    const {
    data: milestoneData,
    isLoading,
    error,
  } = useGroupQuery(
    ['milestones'],
    milestonesApi.getMilestones
  );

  const queryClient = useQueryClient();

  // Milestone status update mutation
  const updateMilestoneStatusMutation = useMutation({
    mutationFn: ({ milestoneId, status, context }: { 
      milestoneId: string; 
      status: MilestoneStatus; 
      context: any 
    }) => milestonesApi.updateMilestoneStatus(milestoneId, status, context),
    onSuccess: (_, { status }) => {
      showSuccessNotification(`Milestone status updated to ${status}`);
      // Invalidate and refetch milestones data
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to update milestone status');
    }
  });

  const objectives = useMemo(() => {
    return milestoneData?.objectives || [];
  }, [milestoneData]);

  // Handle milestone status updates
  const updateMilestoneStatus = async (compositeId: string, status: MilestoneStatus): Promise<void> => {
    // Parse the composite ID: "milestoneId:activityId:goalId:keyActivityId:objectiveId"
    const [milestoneId, activityId, goalId, keyActivityId, objectiveId] = compositeId.split(':');
    
    await updateMilestoneStatusMutation.mutateAsync({
      milestoneId,
      status,
      context: {
        objective_id: objectiveId,
        key_activity_id: keyActivityId,
        goal_id: goalId,
        activity_id: activityId
      }
    });
  };
  
  const allMilestones = useMemo(() => {
    const milestones: Milestone[] = [];
    objectives.forEach(obj => {
      obj.key_activities.forEach(ka => {
        ka.goals.forEach(goal => {
          goal.activities.forEach(activity => {
            milestones.push(...activity.milestones);
          });
        });
      });
    });
    return milestones;
  }, [objectives]);

  const [selectedObjective, setSelectedObjective] = useState<string | null>('all');
  const [selectedStatus, setSelectedStatus] = useState<string | null>('all');

  const objectiveOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Objectives' },
      ...objectives.map(objective => ({
          value: objective.id,
        label: objective.title
      }))
    ];
  }, [objectives]);

  const statusOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Statuses' },
      ...Object.entries(statusConfig).map(([value, config]) => ({
        value,
        label: config.label
      }))
    ];
  }, []);

  // Calculate completion percentage for milestones
  const calculateCompletionPercentage = (milestones: Milestone[]): number => {
    if (milestones.length === 0) return 0;
    const completedCount = milestones.filter(m => m.status === 'completed').length;
    return Math.round((completedCount / milestones.length) * 100);
  };

  // Filter objectives based on selection and status
  const filteredObjectives = useMemo(() => {
    let filtered = objectives;
    
    // Filter by objective
    if (selectedObjective !== 'all') {
      filtered = filtered.filter(objective => objective.id === selectedObjective);
    }

    // Filter by status if a specific status is selected
    if (selectedStatus !== 'all') {
      filtered = filtered.map(objective => ({
        ...objective,
        key_activities: objective.key_activities.map(keyActivity => ({
          ...keyActivity,
          goals: keyActivity.goals.map(goal => ({
            ...goal,
            activities: goal.activities.map(activity => ({
              ...activity,
              milestones: activity.milestones.filter(milestone => 
                milestone.status === selectedStatus
              )
            })).filter(activity => activity.milestones.length > 0)
          })).filter(goal => goal.activities.length > 0)
        })).filter(keyActivity => keyActivity.goals.length > 0)
      })).filter(objective => objective.key_activities.length > 0);
    }
    
    return filtered;
  }, [objectives, selectedObjective, selectedStatus]);

  // Calculate overall completion percentage
  const overallCompletionPercentage = useMemo(() => {
    return calculateCompletionPercentage(allMilestones);
  }, [allMilestones]);

  const progressLastUpdatedLabel = useMemo(
    () =>
      formatMilestonesLastUpdatedLabel({
        objectives,
        metadata: milestoneData?.metadata,
        metadataBySubawardee: milestoneData?.metadataBySubawardee,
      }),
    [objectives, milestoneData?.metadata, milestoneData?.metadataBySubawardee]
  );

  if (isLoading) {
    return (
      <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
        <PageHeader
          title="Milestones"
          subtitle="Track progress and monitor status of project milestones. Milestones are organized under their associated objective and key activity."
        />
        
        <Group position="apart" mb="xl" sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch'
          }
        })}>
          <Stack spacing="md" style={{ flex: 1 }}>
            <Paper 
              p="md" 
              shadow="md" 
              radius="md" 
              sx={(theme) => ({
                border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
                boxShadow: theme.colorScheme === 'dark' 
                  ? '0 0 12px rgba(255, 255, 255, 0.1)' 
                  : theme.shadows.md,
                '@media (max-width: 768px)': {
                  margin: theme.spacing.sm,
                }
              })}
            >
              <Group position="apart" sx={(theme) => ({
                '@media (max-width: 480px)': {
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: theme.spacing.md
                }
              })}>
                <div>
                  <Skeleton height={28} width={150} mb="xs" />
                  <Skeleton height={20} width={200} />
                </div>
                <Skeleton height={120} width={120} radius="50%" />
              </Group>
            </Paper>
            
            <Paper 
              p="md" 
              shadow="md" 
              radius="md" 
              sx={(theme) => ({
                maxWidth: 800,
                border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
                boxShadow: theme.colorScheme === 'dark' 
                  ? '0 0 12px rgba(255, 255, 255, 0.1)' 
                  : theme.shadows.md,
                '@media (max-width: 768px)': {
                  margin: theme.spacing.sm,
                  maxWidth: 'none'
                }
              })}
            >
              <Skeleton height={20} width={60} mb="md" />
              <Group spacing="md" sx={(theme) => ({
                '@media (max-width: 768px)': {
                  flexDirection: 'column',
                  alignItems: 'stretch'
                }
              })}>
                <Skeleton height={40} width={200} sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    width: '100%'
                  }
                })} />
                <Skeleton height={40} width={200} sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    width: '100%'
                  }
                })} />
              </Group>
            </Paper>
          </Stack>
        </Group>

        <Stack spacing="xl" sx={(theme) => ({
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
          }
        })}>
          <ObjectiveSkeleton />
          <ObjectiveSkeleton />
          <ObjectiveSkeleton />
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" mt="xl" pb={80} px={0}>
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          title="Error" 
          color="red"
          role="alert"
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            }
          })}
        >
{error instanceof Error ? error.message : String(error)}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader
        title="Milestones"
        subtitle="Track progress and monitor status of project milestones. Milestones are organized under their associated objective and key activity."
      />
      
      <Group position="apart" mb="xl" sx={(theme) => ({
        '@media (max-width: 768px)': {
          flexDirection: 'column',
          alignItems: 'stretch'
        }
      })}>
        <Stack spacing="md" style={{ flex: 1 }}>
          <Paper 
            p="md" 
            shadow="md" 
            radius="md" 
            sx={(theme) => ({
              border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
              boxShadow: theme.colorScheme === 'dark' 
                ? '0 0 12px rgba(255, 255, 255, 0.1)' 
                : theme.shadows.md,
              '@media (max-width: 768px)': {
                margin: theme.spacing.sm,
              }
            })}
          >
            <Group position="apart" sx={(theme) => ({
              '@media (max-width: 480px)': {
                flexDirection: 'column',
                alignItems: 'center',
                gap: theme.spacing.md
              }
            })}>
              <div>
                <Text size="lg" weight={700}>Overall Progress</Text>
                <Text size="sm" color="dimmed">{`${overallCompletionPercentage}% of milestones completed`}</Text>
                {progressLastUpdatedLabel ? (
                  <Text size="sm" color="dimmed">
                    {progressLastUpdatedLabel}
                  </Text>
                ) : null}
              </div>
              <RingProgress
                size={120}
                thickness={12}
                roundCaps
                sections={[
                  { value: overallCompletionPercentage, color: overallCompletionPercentage === 100 ? 'green' : 'blue' }
                ]}
                label={
                  <Center>
                    <Text size="xl" weight={700}>
                      {overallCompletionPercentage}%
                    </Text>
                  </Center>
                }
              />
            </Group>
          </Paper>
          
          <Paper 
            p="md" 
            shadow="md" 
            radius="md" 
            sx={(theme) => ({
              maxWidth: 800,
              border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
              boxShadow: theme.colorScheme === 'dark' 
                ? '0 0 12px rgba(255, 255, 255, 0.1)' 
                : theme.shadows.md,
              '@media (max-width: 768px)': {
                margin: theme.spacing.sm,
                maxWidth: 'none'
              }
            })}
          >
            <Group spacing="md" sx={(theme) => ({
              '@media (max-width: 768px)': {
                flexDirection: 'column',
                alignItems: 'stretch'
              }
            })}>
            <Select
              label="Filter by Objective"
              placeholder="Select an objective"
              data={objectiveOptions}
              value={selectedObjective}
              onChange={setSelectedObjective}
                style={{ flex: 1, minWidth: 200 }}
                styles={(theme) => ({
                  label: {
                    marginBottom: '8px'
                  },
                  item: {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    lineHeight: '1.4',
                    padding: '8px 12px',
                    minHeight: 'auto',
                  },
                  dropdown: {
                    maxWidth: '400px',
                  },
                  root: {
                    '@media (max-width: 768px)': {
                      width: '100%',
                      minWidth: 'auto'
                    }
                  }
                })}
                maxDropdownHeight={300}
              />
              <Select
                label="Filter by Status"
                placeholder="Select a status"
                data={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                style={{ flex: 1, minWidth: 200 }}
                styles={(theme) => ({
                  label: {
                    marginBottom: '8px'
                  },
                  item: {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    lineHeight: '1.4',
                    padding: '8px 12px',
                    minHeight: 'auto',
                  },
                  dropdown: {
                    maxWidth: '300px',
                  },
                  root: {
                    '@media (max-width: 768px)': {
                      width: '100%',
                      minWidth: 'auto'
                    }
                  }
                })}
                maxDropdownHeight={300}
              />
            </Group>
          </Paper>
        </Stack>
      </Group>

      {filteredObjectives.length > 0 ? (
        <Stack spacing="xl" sx={(theme) => ({
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
          }
        })}>
          {filteredObjectives.map(objective => {
            const originalObjective = objectives.find(orig => orig.id === objective.id);
            return (
              <ObjectiveCard
                key={objective.id}
                objective={objective}
                originalObjective={originalObjective || objective}
                onStatusChange={updateMilestoneStatus}
              />
            );
          })}
        </Stack>
      ) : (
        <Text align="center" size="lg" color="dimmed" sx={(theme) => ({
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
            fontSize: theme.fontSizes.md
          }
        })}>
          {selectedStatus !== 'all' && selectedObjective !== 'all' 
            ? `No milestones found with status "${statusConfig[selectedStatus as MilestoneStatus]?.label || selectedStatus}" in the selected objective.`
            : selectedStatus !== 'all'
            ? `No milestones found with status "${statusConfig[selectedStatus as MilestoneStatus]?.label || selectedStatus}".`
            : selectedObjective !== 'all'
            ? 'No milestones found in the selected objective.'
            : 'No objectives found.'
        }
        </Text>
      )}
    </Container>
  );
} 